import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useBrands } from "@/lib/BrandsProvider";
import { supabase } from "@/lib/supabase";
import { Database } from "@/database.types";

// Use current_content_versions view type
type CurrentContentVersion = Database['public']['Views']['current_content_versions']['Row'];

interface BlogPost extends CurrentContentVersion {
  content_json: {
    title?: string;
    primary_content?: string;
    content_sections?: Array<{
      heading: string;
      content: string;
    }>;
    featured_image?: string;
    image_url?: string;
    bonus_metadata?: {
      excerpt?: string;
      author?: string;
      categories?: string[];
      tags?: string[];
      reading_time?: string;
    };
  };
}

export default function InternalBlogList() {
  const { brandId } = useParams<{ brandId: string }>();
  const { selectedBrand } = useBrands();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use brandId from URL params or fall back to selectedBrand
  const currentBrandId = brandId || selectedBrand?.id;

  useEffect(() => {
    async function fetchBlogPosts() {
      if (!currentBrandId) {
        setError("No brand selected");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from('current_content_versions')
          .select('*')
          .eq('brand_id', currentBrandId)
          .eq('content_type', 'blog_post')
          .eq('content_status', 'PUBLISHED')
          .order('created_at', { ascending: false });

        if (fetchError) {
          throw fetchError;
        }

        setPosts((data as BlogPost[]) || []);
      } catch (err) {
        console.error("Error fetching blog posts:", err);
        setError(err instanceof Error ? err.message : "An unknown error occurred");
      } finally {
        setLoading(false);
      }
    }

    fetchBlogPosts();
  }, [currentBrandId]);

  // Format date
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch (err) {
      return dateString;
    }
  };

  if (!currentBrandId) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground text-center">
              Please select a brand to view blog posts.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-2xl font-bold">Blog Posts</h1>
        <p className="text-muted-foreground">
          Published blog posts for {selectedBrand?.brand_name || 'this brand'}
        </p>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center items-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : error ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-red-500 text-center">
              Error loading blog posts: {error}
            </p>
          </CardContent>
        </Card>
      ) : posts.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                <span className="text-2xl">📝</span>
              </div>
              <h3 className="text-lg font-medium mb-2">No blog posts yet</h3>
              <p className="text-muted-foreground">
                Create and publish your first blog post to see it here.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6">
          {posts.map((post) => (
            <Card key={post.id} className="overflow-hidden hover:shadow-lg transition-all duration-300 hover:border-primary/20 group">
              {(post.content_json?.featured_image || post.content_json?.image_url) ? (
                <div className="aspect-video overflow-hidden bg-gradient-to-br from-muted/50 to-muted/30">
                  <img 
                    src={post.content_json?.featured_image || post.content_json?.image_url} 
                    alt={post.content_json?.title || post.name || 'Blog post'}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                </div>
              ) : (
                <div className="aspect-video bg-gradient-to-br from-primary/10 via-primary/5 to-transparent flex items-center justify-center">
                  <div className="text-center p-6">
                    <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-2xl">📝</span>
                    </div>
                    <h3 className="text-lg font-medium text-primary/80 line-clamp-2">
                      {post.content_json?.title || post.name}
                    </h3>
                  </div>
                </div>
              )}
              
              <CardHeader>
                <div className="space-y-3">
                  {post.content_json?.bonus_metadata?.categories && post.content_json.bonus_metadata.categories.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {post.content_json.bonus_metadata.categories.slice(0, 3).map((category, index) => (
                        <span 
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                        >
                          {category}
                        </span>
                      ))}
                      {post.content_json?.bonus_metadata?.categories && post.content_json.bonus_metadata.categories.length > 3 && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                          +{post.content_json.bonus_metadata.categories.length - 3} more
                        </span>
                      )}
                    </div>
                  )}
                  <CardTitle className="text-xl md:text-2xl leading-tight font-bold">
                    <Link 
                      to={`/brands/${currentBrandId}/blog/${post.id}`} 
                      className="hover:text-primary transition-colors group-hover:text-primary/90"
                    >
                      {post.content_json?.title || post.name}
                    </Link>
                  </CardTitle>
                </div>
              </CardHeader>

              <CardContent className="pb-4">
                {post.content_json?.bonus_metadata?.excerpt ? (
                  <p className="text-muted-foreground text-base leading-relaxed line-clamp-3">
                    {post.content_json.bonus_metadata.excerpt}
                  </p>
                ) : post.content_json?.primary_content ? (
                  <div 
                    className="text-muted-foreground text-base leading-relaxed line-clamp-3"
                    style={{ 
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}
                  >
                    {post.content_json?.primary_content?.substring(0, 200)}...
                  </div>
                ) : (
                  <p className="text-muted-foreground text-base leading-relaxed">
                    No preview available
                  </p>
                )}
              </CardContent>

              <CardFooter className="pt-4">
                <div className="flex flex-col gap-3 w-full">
                  {/* Author and reading time row */}
                  {(post.content_json?.bonus_metadata?.author || post.content_json?.bonus_metadata?.reading_time) && (
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      {post.content_json?.bonus_metadata?.author && (
                        <div className="flex items-center gap-1">
                          <span className="font-medium">By</span>
                          <span>{post.content_json.bonus_metadata.author}</span>
                        </div>
                      )}
                      {post.content_json?.bonus_metadata?.reading_time && (
                        <div className="flex items-center gap-1">
                          <span>📖</span>
                          <span>{post.content_json.bonus_metadata.reading_time}</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Date and read more row */}
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">
                      {formatDate(post.created_at || '')}
                    </span>
                    <Link 
                      to={`/brands/${currentBrandId}/blog/${post.id}`} 
                      className="inline-flex items-center text-primary hover:text-primary/80 transition-colors text-sm font-medium group"
                    >
                      Read more 
                      <span className="ml-1 group-hover:translate-x-0.5 transition-transform">→</span>
                    </Link>
                  </div>
                </div>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}